<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :isFilter="false"
                        :title="$t('catering_request_detail')"/>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :isFilter="false"
                              :title="$t('catering_request_detail')"/>
            </template>

            <div class="container-fluid px-0 mb-5" v-if="cateringRequestData.id" lang="en">

                <!-- Action Header Row -->
                <div class="row mb-4">
                    <div class="col-12 d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <b-badge :variant="getBadgeVariant(cateringRequestData.status)" class="px-3 py-2 badge-pill text-uppercase mr-3 font-weight-bold">
                                {{ cateringRequestData.status_name }}
                            </b-badge>
                            <span class="text-muted small">
                                <i class="ri-calendar-line mr-1"></i>{{ $t('created_at') }}: {{ cateringRequestData.created_at }}
                            </span>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-button
                                v-if="checkPermission('cateringform_auditpdf')"
                                variant="outline-danger"
                                class="d-flex align-items-center px-4 py-2 border-2 mr-2"
                                @click="downloadAuditPdf"
                                :disabled="isDownloadingPdf"
                            >
                                <b-spinner small class="mr-2" v-if="isDownloadingPdf" />
                                <i class="ri-file-pdf-line mr-2 ri-lg" v-else></i>
                                <span class="font-weight-bold">{{ $t('export_as_pdf') }}</span>
                            </b-button>
                            <b-button variant="outline-primary" class="d-flex align-items-center px-4 py-2 border-2" @click="$router.push('/my-staff-requests/0')">
                                <i class="ri-arrow-left-line mr-2 ri-lg"></i>
                                <span class="font-weight-bold">{{ $t('back').toUpperCase() }}</span>
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- Academic Metric Row (Catering Info Cards) -->
                <div class="row mb-4">
                    <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                        <div class="metric-card bg-gradient-blue text-white p-3 rounded-lg shadow-sm">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="metric-label">{{ toUpperCase('guest_count') }}</span>
                                <div class="metric-icon-box"><i class="ri-team-line"></i></div>
                            </div>
                            <h2 class="metric-value mb-0">{{ cateringRequestData.guest_count || '-' }}</h2>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                        <div class="metric-card bg-gradient-green text-white p-3 rounded-lg shadow-sm">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="metric-label">{{ toUpperCase('budget') }}</span>
                                <div class="metric-icon-box"><i class="ri-money-dollar-circle-line"></i></div>
                            </div>
                            <h2 class="metric-value mb-0">{{ this.formatToCurrency(cateringRequestData.budget || '-') }}</h2>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                        <div class="metric-card bg-gradient-purple text-white p-3 rounded-lg shadow-sm">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="metric-label">{{ toUpperCase('grand_total') }}</span>
                                <div class="metric-icon-box"><i class="ri-calculator-line"></i></div>
                            </div>
                            <h2 class="metric-value mb-0">{{ this.formatToCurrency(cateringRequestData.grand_total) }}</h2>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                        <div class="metric-card bg-gradient-orange text-white p-3 rounded-lg shadow-sm">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="metric-label">{{ toUpperCase('menu_items') }}</span>
                                <div class="metric-icon-box"><i class="ri-list-check"></i></div>
                            </div>
                            <h2 class="metric-value mb-0">{{ (cateringRequestData.menu_items && cateringRequestData.menu_items.length) }}</h2>
                        </div>
                    </div>
                </div>

              <div class="row mb-4">
                <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                  <div class="metric-card bg-gradient-blue text-white p-3 rounded-lg shadow-sm">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="metric-label">TOTAL ALLOCATED BUDGET</span>
                      <div class="metric-icon-box"><i class="ri-money-dollar-circle-line"></i></div>
                    </div>
                    <h2 class="metric-value mb-0">{{ this.formatToCurrency(cateringRequestData.budget_summary.budget || '-') }}</h2>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                  <div class="metric-card bg-gradient-green text-white p-3 rounded-lg shadow-sm">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="metric-label">SPENT BUDGET</span>
                      <div class="metric-icon-box"><i class="ri-money-dollar-circle-line"></i></div>
                    </div>
                    <h2 class="metric-value mb-0">{{ this.formatToCurrency(cateringRequestData.budget_summary.spent || '-') }}</h2>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                  <div class="metric-card bg-gradient-purple text-white p-3 rounded-lg shadow-sm">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="metric-label">AVAILABLE BUDGET</span>
                      <div class="metric-icon-box"><i class="ri-money-dollar-circle-line"></i></div>
                    </div>
                    <h2 class="metric-value mb-0">{{ this.formatToCurrency(cateringRequestData.budget_summary.remaining || '-') }}</h2>
                  </div>
                </div>
              </div>

                <!-- Profile and Program Details Section -->
                <div class="row mb-4">
                    <!-- Student Profile Card -->
                    <div class="col-12 col-lg-4 mb-4 mb-lg-0">
                        <div class="card h-100 shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="profile-header-bg"></div>
                            <div class="card-body pt-0 text-center position-relative">
                                <div class="profile-avatar-wrapper shadow">
                                    <span class="avatar-initials">{{ getInitials(cateringRequestData.employee_name) }}</span>
                                </div>
                                <h4 class="font-weight-bold text-dark mb-1 text-capitalize mt-3">{{ cateringRequestData.employee_name }}</h4>

                                <div class="pt-3 border-top text-left mt-3">
                                    <div class="mb-3 d-flex align-items-center">
                                        <i class="ri-user-2-line text-theme mr-3 ri-xl"></i>
                                        <div>
                                            <span class="profile-meta-label">{{ toUpperCase('employee_id') }}</span>
                                            <div class="profile-meta-value">{{ cateringRequestData.employee_id || '-' }}</div>
                                        </div>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <i class="ri-shield-user-line text-theme mr-3 ri-xl"></i>
                                        <div>
                                            <span class="profile-meta-label">{{ toUpperCase('job_title') }}</span>
                                            <div class="profile-meta-value">{{ cateringRequestData.job_title_name || '-' }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Academic Extra Details -->
                    <div class="col-12 col-lg-8">
                        <div class="card h-100 shadow-sm border-0 rounded-lg">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-4 pb-3 border-bottom">
                                    <div class="icon-wrapper bg-soft-theme mr-3">
                                        <i class="ri-calendar-event-line text-theme ri-xl"></i>
                                    </div>
                                    <h5 class="card-title mb-0 text-dark font-weight-bold">{{ $t('event_information') }}</h5>
                                </div>

                                <div class="row">
                                    <!-- Event -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('event') }}</span>
                                            <div class="info-value text-dark">
                                                {{ cateringRequestData.event_label || cateringRequestData.event || '-' }}
                                                <span v-if="cateringRequestData.event_specify" class="text-muted small d-block">
                                                    ({{ cateringRequestData.event_specify }})
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Account Number -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('account_number') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.account_number || '-' }}</div>
                                        </div>
                                    </div>
                                    <!-- Account Name -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('account_name') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.account_name || '-' }}</div>
                                        </div>
                                    </div>
                                    <!-- Audience -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('audience') }}</span>
                                            <div class="info-value text-dark">
                                                {{ cateringRequestData.audience_label || cateringRequestData.audience || '-' }}
                                                <span v-if="cateringRequestData.audience_specify" class="text-muted small d-block">
                                                    ({{ cateringRequestData.audience_specify }})
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Class -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('class') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.class_label || cateringRequestData.class || '-' }}</div>
                                        </div>
                                    </div>
                                    <!-- Location (Financial) -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('location') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.location_label || cateringRequestData.location || '-' }}</div>
                                        </div>
                                    </div>
                                    <!-- Event Location (Physical Venue) -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('event_location') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.event_location || '-' }}</div>
                                        </div>
                                    </div>
                                    <!-- Event Date -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('event_date') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.event_date || '-' }}</div>
                                        </div>
                                    </div>
                                    <!-- Event Time -->
                                    <div class="col-sm-6 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ toUpperCase('event_time') }}</span>
                                            <div class="info-value text-dark">{{ cateringRequestData.event_time || '-' }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Visual Program Comparison Layout (Modified for Dietary & Notes) -->
                <div class="row mb-5">
                    <div class="col-12">
                        <div class="card shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="card-header bg-light-blue p-4 border-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-wrapper bg-soft-theme mr-3">
                                        <i class="ri-chat-quote-line ri-xl"></i>
                                    </div>
                                    <h5 class="mb-0 text-dark font-weight-bold">{{ $t('catering_details') }}</h5>
                                </div>
                            </div>
                            <div class="card-body p-4 bg-light-gray">
                                <div class="row align-items-stretch">
                                    <!-- Dietary Restrictions Card -->
                                    <div class="col-12 col-md-6">
                                        <div class="program-box current-program h-100 p-4 bg-white rounded-lg shadow-sm border-top-primary">
                                            <div class="program-tag text-primary">{{ $t('dietary_restrictions').toUpperCase() }}</div>
                                            <div class="detail-item mt-3 text-dark font-italic">
                                                {{ cateringRequestData.dietary_restrictions || '-' }}
                                            </div>
                                            <div class="program-details mt-4 pt-3 border-top">
                                                <div class="detail-item">
                                                    <span><b>{{ $t('dish_type') }}:</b> {{ cateringRequestData.dish_type_name || '-' }}</span>
                                                </div>
                                                <div class="detail-item mb-0">
                                                    <span><b>{{ $t('catering_setup_time') }}:</b> {{ cateringRequestData.catering_setup_time || '-' }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Target Program Card (Requester Notes) -->
                                    <div class="col-12 col-md-6">
                                        <div class="program-box target-program h-100 p-4 bg-white rounded-lg shadow-sm border-top-success">
                                            <div class="program-tag text-success">{{ $t('requester_notes').toUpperCase() }}</div>
                                            <div class="detail-item mt-3 text-dark font-italic">
                                                {{ cateringRequestData.requester_notes || '-' }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Menu Items List -->
                <div class="row mb-5" v-if="cateringRequestData.menu_items && cateringRequestData.menu_items.length">
                    <div class="col-12">
                        <div class="card shadow-sm border-0 rounded-lg overflow-hidden" ref="menuItemsCard">
                            <div class="card-header bg-light p-4 border-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-wrapper bg-soft-theme mr-3">
                                        <i class="ri-list-check-2 ri-xl"></i>
                                    </div>
                                    <h5 class="mb-0 text-dark font-weight-bold">{{ $t('menu_items') }}</h5>
                                </div>
                            </div>
                            <div class="card-body p-4 bg-white">
                                <b-table :fields="computeMenuTableFields" :items="cateringRequestData.menu_items" bordered striped responsive class="mb-0">
                                    <template #cell(ppu)="data">
                                        {{ data.item.ppu != null ? data.item.ppu : '-' }}
                                    </template>
                                    <template #cell(total)="data">
                                        {{ data.item.total != null ? data.item.total : '-' }}
                                    </template>
                                </b-table>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Approval Decision Form (Active Step Actions) -->

                <!-- Modern Workflow Timeline Section -->
                <div class="row" v-if="steps && steps.length">
                    <div class="col-12">
                        <div class="card shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="card-header bg-light p-4 border-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-wrapper bg-soft-dark mr-3">
                                        <i class="ri-git-commit-line text-dark ri-xl"></i>
                                    </div>
                                    <h5 class="mb-0 text-dark font-weight-bold">
                                        {{ $t('approval_procedures') }}
                                    </h5>
                                </div>
                            </div>
                            <div class="card-body p-4 bg-white">
                                <approval-timeline
                                    :steps="steps"
                                    :selected-active-step="selectedActiveStep"
                                    :allow-edit="allowEdit"
                                    :is-loading="isLoading"
                                    :statuses="statuses"
                                    :status.sync="updateForm.status"
                                    :reason.sync="updateForm.reason"
                                    :permission="activeStepPermission"
                                    @save="stepUpdate"
                                >
                                    <!-- PPU Fill — sadece rank 1 (waiting_po_review / waiting_po_approval) aktifken -->
                                    <template #step-1>
                                        <div v-if="canFillPpu">
                                            <ValidationObserver ref="ppuForm">
                                                <span class="d-block font-weight-bold text-dark mb-2">
                                                    <i class="ri-price-tag-3-line mr-1 text-primary"></i>
                                                    {{ $t('menu_items') }} — PPU
                                                </span>
                                                <b-table
                                                    :fields="computeMenuTableFields"
                                                    :items="cateringRequestData.menu_items"
                                                    bordered
                                                    small
                                                    responsive
                                                    class="mb-2"
                                                >
                                                    <template #cell(ppu)="data">
                                                        <ValidationProvider
                                                            :name="'ppu_' + data.index"
                                                            rules="required"
                                                            v-slot="{ errors }"
                                                        >
                                                            <b-form-input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                v-model="ppuItems[data.index].ppu"
                                                                size="sm"
                                                                :state="errors[0] ? false : null"
                                                            />
                                                            <div class="invalid-feedback d-block mt-1" v-if="errors[0]" v-html="errors[0]"></div>
                                                        </ValidationProvider>
                                                    </template>
                                                    <template #cell(total)="data">
                                                        {{ data.item.total != null ? data.item.total : '-' }}
                                                    </template>
                                                </b-table>
                                            </ValidationObserver>
                                        </div>
                                    </template>

                                    <!-- Requester Sign Form -->
                                    <template #step-2>
                                        <div v-if="selectedActiveStep">
                                            <ValidationObserver ref="signForm">
                                                <!-- Budget Status -->
                                                <div class="mb-4">
                                                    <ValidationProvider name="budget_status" rules="required" v-slot="{ errors }">
                                                        <b-form-group :label="$t('budget_status')" class="mb-1">
                                                            <multiselect
                                                                v-model="signForm.budget_status"
                                                                :options="budgetStatusOptions"
                                                                :custom-label="budgetStatusLabel"
                                                                :placeholder="$t('select')"
                                                                :select-label="''"
                                                                :selected-label="''"
                                                                :deselect-label="''"
                                                                :searchable="false"
                                                                class="mt-1"
                                                            />
                                                            <div class="invalid-feedback d-block mt-1" v-if="errors[0]" v-html="errors[0]"></div>
                                                        </b-form-group>
                                                    </ValidationProvider>
                                                </div>

                                                <!-- Menu Items Quantity -->
                                                <div v-if="signForm.menuItems && signForm.menuItems.length">
                                                    <label class="font-weight-bold d-block mb-2">
                                                        {{ $t('menu_items') }}
                                                    </label>
                                                    <b-table
                                                        :items="signForm.menuItems"
                                                        :fields="menuItemFields"
                                                        bordered
                                                        small
                                                        responsive
                                                        class="mb-3"
                                                    >
                                                        <template #cell(quantity)="row">
                                                            <ValidationProvider
                                                                :name="'quantity_' + row.index"
                                                                rules="required"
                                                                v-slot="{ errors }"
                                                            >
                                                                <b-form-input
                                                                    type="number"
                                                                    min="1"
                                                                    v-model.number="signForm.menuItems[row.index].quantity"
                                                                    size="sm"
                                                                    style="max-width: 100px;"
                                                                />
                                                                <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                                            </ValidationProvider>
                                                        </template>
                                                        <template #cell(ppu)="row">
                                                            {{ row.item.ppu != null ? row.item.ppu : '-' }}
                                                        </template>
                                                    </b-table>
                                                </div>
                                            </ValidationObserver>
                                        </div>
                                    </template>

                                    <!-- Invoice Upload — rank 4 (waiting_po_receipt) active -->
                                    <template #step-4>
                                        <div v-if="cateringRequestData.status === 'waiting_po_receipt' && checkPermission('cateringform_uploadinvoice')">
                                            <div class="mb-3" v-if="cateringRequestData.invoices && cateringRequestData.invoices.length">
                                                <span class="d-block font-weight-bold text-dark mb-2">{{ $t('uploaded_invoices') }}</span>
                                                <div
                                                    v-for="inv in cateringRequestData.invoices"
                                                    :key="inv.uuid"
                                                    class="d-flex align-items-center justify-content-between p-2 mb-1 border rounded bg-light"
                                                >
                                                    <div class="d-flex align-items-center">
                                                        <i class="ri-file-line mr-2 text-primary"></i>
                                                        <span class="small">{{ inv.file_name || inv.uuid }}</span>
                                                    </div>
                                                    <b-button
                                                        variant="link"
                                                        size="sm"
                                                        class="text-danger p-0"
                                                        @click="removeInvoice(inv.uuid)"
                                                    >
                                                        <i class="ri-delete-bin-line"></i>
                                                    </b-button>
                                                </div>
                                            </div>

                                            <div class="d-flex align-items-center flex-wrap">
                                                <b-form-file
                                                    v-model="invoiceFiles"
                                                    multiple
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    :placeholder="$t('choose_files')"
                                                    :browse-text="$t('browse')"
                                                    class="mr-3 mb-2"
                                                    style="max-width: 340px;"
                                                />
                                                <b-button
                                                    variant="success"
                                                    size="md"
                                                    class="d-flex align-items-center font-weight-bold mb-2"
                                                    :disabled="!invoiceFiles || !invoiceFiles.length || isUploadingInvoice"
                                                    @click="uploadInvoices"
                                                >
                                                    <b-spinner small class="mr-2" v-if="isUploadingInvoice" />
                                                    <i class="ri-upload-line mr-2" v-else></i>
                                                    {{ $t('upload_invoice') }}
                                                </b-button>
                                            </div>
                                        </div>
                                    </template>
                                </approval-timeline>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </app-layout>
    </div>
</template>

<script>
// Template
import AppLayout from "@/layouts/AppLayout";
import Header from "@/layouts/AppLayout/Header";
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";

// Services
import CateringRequestService from "@/services/CateringRequestService";

// Components
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ApprovalTimeline from "@/components/elements/ApprovalTimeline.vue";

export default {
    name: "CateringRequestDetail",
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        ValidationObserver,
        ValidationProvider,
        ApprovalTimeline
    },
    metaInfo() {
        return {
            title: this.$t('catering_request_detail')
        }
    },
    data() {
        return {
            id: this.$route.params.id,
            cateringRequestData: {},
            steps: [],
            statuses: {},
            updateForm: {
                id: null,
                status: null,
                reason: null,
            },
            ppuItems: [],
            isLoading: false,
            isPpuSaving: false,
            invoiceFiles: [],
            isUploadingInvoice: false,
            signForm: {
                budget_status: null,
                menuItems: [],
            },
            isSigning: false,
            isDownloadingPdf: false,
        }
    },
    created() {
        this.getData();
    },
    computed: {
        userRoles() {
            return this.$store.getters['auth/getActiveRole']
        },
        allowEdit() {
            return !['approved', 'declined'].includes(this.cateringRequestData.status);
        },
        selectedActiveStep() {
            if (this.cateringRequestData.active_step) {
                const activeStep = this.cateringRequestData.active_step;
                if (!activeStep.approvers || activeStep.approvers.length === 0) {
                    return false;
                }
                const user = this.$store.getters["auth/getUser"];
                return activeStep.approvers.some(approver =>
                    approver.name === this.userRoles ||
                    approver.id === user.id ||
                    approver.user_id === user.id ||
                    (approver.name === user.name && approver.surname === user.surname) ||
                    (user.person && approver.name === user.person.name && approver.surname === user.person.surname)
                );
            }
            return false;
        },
        canFillPpu() {
            const status = this.cateringRequestData.status;
            const activeStep = this.cateringRequestData.active_step;
            const isPOStep = activeStep && activeStep.rank === 1;
            const isCorrectStatus = ['waiting_po_review', 'waiting_po_approval'].includes(status);
            return isPOStep && isCorrectStatus && this.selectedActiveStep && this.checkPermission('cateringform_purchaseofficerfill');
        },
        computeMenuTableFields() {
            return [
                { key: 'name', label: this.toUpperCase('item_name') },
                { key: 'quantity', label: this.toUpperCase('quantity'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'ppu', label: this.toUpperCase('ppu'), thClass: 'text-right', tdClass: this.canFillPpu ? 'text-right' : 'text-right' },
                { key: 'total', label: this.toUpperCase('total'), thClass: 'text-right', tdClass: 'text-right' }
            ];
        },
        budgetStatusOptions() {
            return ['budgeted', 'not_budgeted'];
        },
        menuItemFields() {
            return [
                { key: "name", label: this.toUpperCase("item_name") },
                { key: "quantity", label: this.toUpperCase("quantity"), thClass: "text-center", tdClass: "text-center" },
                { key: "ppu", label: this.toUpperCase("ppu"), thClass: "text-right", tdClass: "text-right" }
            ];
        },
        activeStepPermission() {
            const activeStep = this.cateringRequestData.active_step;
            if (activeStep && activeStep.rank === 2) {
                return 'cateringform_requestersign';
            }
            return 'approvalstep_updatestatus';
        },
    },
    methods: {
        downloadAuditPdf() {
            this.isDownloadingPdf = true;
            CateringRequestService.downloadAuditPdf(this.id)
                .then((res) => {
                    this._downloadFile(res, `catering_request_${this.id}_audit.pdf`);
                })
                .catch((err) => {
                    this.showErrors(err);
                })
                .finally(() => {
                    this.isDownloadingPdf = false;
                });
        },
        getData() {
            CateringRequestService.get(this.id)
                .then((response) => {
                    this.cateringRequestData = response.data.data;
                    this.steps = (this.cateringRequestData.steps ?? []).sort((a, b) => a.rank - b.rank);
                    if (this.cateringRequestData.active_step != null) {
                        this.updateForm.id = this.cateringRequestData.active_step.id;
                        this.statuses = this.cateringRequestData.active_step.statuses ?? {};
                    }
                    this.ppuItems = (this.cateringRequestData.menu_items ?? []).map(item => ({
                        id: item.id,
                        ppu: item.ppu != null ? item.ppu : ''
                    }));
                    this.signForm.budget_status = this.cateringRequestData.budget_status || null;
                    this.signForm.menuItems = (this.cateringRequestData.menu_items ?? []).map(item => ({
                        id: item.id,
                        name: item.name,
                        quantity: item.quantity,
                        ppu: item.ppu
                    }));
                })
                .catch((error) => {
                    this.showErrors(error);
                });
        },
        uploadInvoices() {
            if (!this.invoiceFiles || !this.invoiceFiles.length) return;
            this.isUploadingInvoice = true;
            const formData = new FormData();
            this.invoiceFiles.forEach(file => {
                formData.append('invoices[]', file);
            });
            CateringRequestService.uploadInvoices(this.id, formData)
                .then((response) => {
                    this.$toast.success(this.$t('api.' + response.data.message));
                    this.invoiceFiles = [];
                    this.getData();
                })
                .catch(error => {
                    this.showErrors(error);
                })
                .finally(() => {
                    this.isUploadingInvoice = false;
                });
        },
        removeInvoice(uuid) {
            this.$swal.fire({
                title: this.$t('warning'),
                text: this.$t('are_you_sure_to_delete'),
                showCancelButton: true,
                confirmButtonText: this.$t('yes'),
                cancelButtonText: this.$t('no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    CateringRequestService.deleteInvoice(this.id, uuid)
                        .then((response) => {
                            this.$toast.success(this.$t('api.' + response.data.message));
                            this.getData();
                        })
                        .catch(error => {
                            this.showErrors(error);
                        });
                }
            });
        },
        stepUpdate() {
            const activeStep = this.cateringRequestData.active_step;
            const isRank1Approve = activeStep && activeStep.rank === 1 && this.updateForm.status === 'approved';
            const isRank2Approve = activeStep && activeStep.rank === 2 && this.updateForm.status === 'approved';

            const update = () => {
                this.$swal.fire({
                    title: this.$t('warning'),
                    text: this.$t('are_you_sure_to_proceed'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no'),
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.isLoading = true;

                        if (isRank1Approve) {
                            const ppuPayload = {
                                menu_items: this.ppuItems.map(item => ({
                                    id: item.id,
                                    ppu: parseFloat(item.ppu)
                                }))
                            };

                            CateringRequestService.purchaseOfficerFill(this.id, ppuPayload)
                                .then(() => {
                                    let payload = { status: this.updateForm.status };
                                    return CateringRequestService.updateStepStatus(this.updateForm.id, payload);
                                })
                                .then(response => {
                                    this.$toast.success(this.$t('api.' + response.data.message));
                                    this.getData();
                                    this.updateForm.status = null;
                                    this.updateForm.reason = null;
                                })
                                .catch(error => {
                                    this.showErrors(error);
                                })
                                .finally(() => {
                                    this.isLoading = false;
                                });
                        } else if (isRank2Approve) {
                            const payload = {
                                budget_status: this.signForm.budget_status,
                            };

                            const originalItems = this.cateringRequestData.menu_items || [];
                            const changedItems = this.signForm.menuItems.filter(
                                (mi, i) => mi.quantity !== (originalItems[i]?.quantity)
                            );
                            if (changedItems.length > 0) {
                                payload.menu_items = changedItems.map((mi) => ({
                                    id: mi.id,
                                    quantity: mi.quantity,
                                }));
                            }

                            CateringRequestService.requesterSign(this.id, payload)
                                .then(() => {
                                    let stepPayload = { status: this.updateForm.status };
                                    return CateringRequestService.updateStepStatus(this.updateForm.id, stepPayload);
                                })
                                .then(response => {
                                    this.$toast.success(this.$t('api.' + response.data.message));
                                    this.getData();
                                    this.updateForm.status = null;
                                    this.updateForm.reason = null;
                                    this.signForm.budget_status = null;
                                    this.signForm.menuItems = [];
                                })
                                .catch(error => {
                                    this.showErrors(error);
                                })
                                .finally(() => {
                                    this.isLoading = false;
                                });
                        } else {
                            let payload = { status: this.updateForm.status };
                            if (this.updateForm.status === 'declined') {
                                payload.reason = this.updateForm.reason;
                            }

                            CateringRequestService.updateStepStatus(this.updateForm.id, payload)
                                .then(response => {
                                    this.$toast.success(this.$t('api.' + response.data.message));
                                    this.getData();
                                    this.updateForm.status = null;
                                    this.updateForm.reason = null;
                                })
                                .catch(error => {
                                    this.showErrors(error);
                                })
                                .finally(() => {
                                    this.isLoading = false;
                                });
                        }
                    }
                });
            };

            if (isRank1Approve) {
                const ppuForm = Array.isArray(this.$refs.ppuForm) ? this.$refs.ppuForm[0] : this.$refs.ppuForm;
                if (ppuForm) {
                    ppuForm.validate().then(valid => {
                        if (valid) {
                            update();
                        } else {
                            this.$toast.error(this.$t('you_must_fill_in_the_fields_indicated_in_red'));
                        }
                    });
                    return;
                }
            }

            if (isRank2Approve) {
                const signForm = Array.isArray(this.$refs.signForm) ? this.$refs.signForm[0] : this.$refs.signForm;
                if (signForm) {
                    signForm.validate().then(valid => {
                        if (valid) {
                            update();
                        } else {
                            this.$toast.error(this.$t('you_must_fill_in_the_fields_indicated_in_red'));
                        }
                    });
                    return;
                }
            }

            update();
        },
        getInitials(name) {
            if (!name) return 'U';
            const parts = name.split(' ');
            return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
        },
        getBadgeVariant(status) {
            switch(status) {
                case 'approved':
                    return 'success';
                case 'declined':
                    return 'danger';
                case 'pending':
                case 'waiting_student_approve':
                case 'waiting_po_review':
                case 'waiting_po_approval':
                case 'waiting_requester_review':
                case 'waiting_cfo_signature':
                case 'waiting_president_signature':
                case 'waiting_po_receipt':
                    return 'warning';
                default:
                    return 'secondary';
            }
        },
        budgetStatusLabel(val) {
            return val ? this.$t(val) : '';
        }
    }
}
</script>

<style scoped>
.font-weight-bold {
    font-weight: 600 !important;
}
.icon-wrapper {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bg-soft-dark {
    background-color: rgba(30, 41, 59, 0.1);
}
.bg-soft-theme {
    background-color: rgba(14, 177, 249, 0.1);
}
.text-theme {
    color: rgb(14, 177, 249) !important;
}
.bg-light-blue {
    background-color: #f0f4f8;
}
.bg-light-gray {
    background-color: #fafbfc;
}
.metric-card {
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #ffffff !important;
}
.metric-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(14, 177, 249, 0.15);
}
.metric-label {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    opacity: 0.9;
    color: #ffffff !important;
}
.metric-value {
    font-size: 2rem;
    font-weight: 800;
    color: #ffffff !important;
}
.metric-icon-box {
    width: 32px;
    height: 32px;
    background-color: rgba(255,255,255,0.25);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
}
.bg-gradient-blue {
    background: linear-gradient(135deg, rgb(14, 177, 249) 0%, #0c9acf 100%);
}
.bg-gradient-green {
    background: linear-gradient(135deg, rgb(14, 177, 249) 0%, #0ca7c1 100%);
}
.bg-gradient-purple {
    background: linear-gradient(135deg, rgb(14, 177, 249) 0%, #066d9c 100%);
}
.bg-gradient-orange {
    background: linear-gradient(135deg, rgb(14, 177, 249) 0%, #008ece 100%);
}

/* Profile Card */
.profile-header-bg {
    height: 90px;
    background: linear-gradient(90deg, rgb(14, 177, 249) 0%, #008ece 100%);
}
.profile-avatar-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: white;
    margin: -40px auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
}
.avatar-initials {
    font-size: 1.8rem;
    font-weight: 800;
    color: rgb(14, 177, 249);
}
.profile-meta-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #8c9ba5;
}
.profile-meta-value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2c3e50;
}


.border-left-theme {
    border-left: 3px solid rgb(14, 177, 249);
}

/* Info Labels */
.info-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #8c9ba5;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 2px;
}
.info-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2c3e50;
}
.border-top-primary {
    border-top: 4px solid rgb(14, 177, 249);
}
.border-top-success {
    border-top: 4px solid #28c76f;
}
.program-box {
    border: 1px solid #e9ebeb;
    transition: all 0.3s ease;
}
.program-box:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}
.program-tag {
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 1px;
}
.detail-item {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    font-size: 0.9rem;
    color: #5d6d7e;
}
.border-2 {
    border-width: 2px !important;
}
</style>
